import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Badge, Button, Modal } from "react-bootstrap";
import swal from "sweetalert";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref,
  uploadBytes,
} from "firebase/storage";

import { db, storage } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import PageTitle from "../../layouts/PageTitle";
import Toast, {
  notifyError,
  notifyTopRight,
  notifyWarning,
} from "../toast/SetToast";
import {
  canEditFeatureRequest,
  canManageFeatureRequestClassification,
  canManageFeatureRequestStatus,
  DEFAULT_FEATURE_REQUEST_PRIORITY,
  FEATURE_REQUEST_PRIORITIES,
  isFeatureRequestAdmin,
  resolveFeatureRequestPriority,
} from "./featureRequestPermissions";
import {
  FEATURE_REQUEST_STATUSES,
  requiresFeatureRequestResolutionComment,
  sortFeatureRequestsForUser,
} from "./featureRequestSorting";

const COLLECTION_NAME = "featureRequests";

const PRODUCT_AREAS = [
  "PSG Software",
  "PSG Hardware",
  "BDSleep",
  "Automation",
  "Other / Not sure",
];
const REQUEST_TYPES = [
  "New Feature",
  "Improve Feature",
  "Fix Bug",
  "Other / Not sure",
];
const PRIORITIES = FEATURE_REQUEST_PRIORITIES;
const STATUSES = FEATURE_REQUEST_STATUSES;
const MAX_RESOLUTION_COMMENT_LENGTH = 1500;

const EMPTY_FORM = {
  title: "",
  description: "",
  productArea: "BDSleep",
  requestType: "New Feature",
  priority: DEFAULT_FEATURE_REQUEST_PRIORITY,
};

const priorityVariant = (priority) => {
  switch (priority) {
    case "Critical":
      return "danger";
    case "High":
      return "warning";
    case "Medium":
      return "primary";
    case "Low":
      return "secondary";
    default:
      return "secondary";
  }
};

const statusVariant = (status) => {
  switch (status) {
    case "Completed":
      return "success";
    case "In Progress":
      return "primary";
    case "Planned":
      return "info";
    case "Under Review":
      return "warning";
    case "Declined":
      return "dark";
    default:
      return "secondary";
  }
};

const getDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = getDate(value);
  if (!date) return "-";

  return date.toLocaleString("th-TH", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getFileExtension = (file) => {
  const fileName = (file && (file.fileName || file.savedFileName)) || "";
  const parts = fileName.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const isImageFile = (file) => {
  if (file && file.contentType && file.contentType.indexOf("image/") === 0) {
    return true;
  }

  return ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(
    getFileExtension(file),
  );
};

const isOfficeFile = (file) =>
  ["doc", "docx", "xls", "xlsx"].includes(getFileExtension(file));

const deleteStorageFolder = async (folderRef) => {
  const folderContents = await listAll(folderRef);
  const nestedFolderResults = await Promise.all(
    folderContents.prefixes.map((prefixRef) => deleteStorageFolder(prefixRef)),
  );
  const fileResults = await Promise.all(
    folderContents.items.map(async (fileRef) => {
      try {
        await deleteObject(fileRef);
        return true;
      } catch (error) {
        console.error("Unable to delete request attachment", error);
        return false;
      }
    }),
  );

  return [...nestedFolderResults, ...fileResults].every(Boolean);
};

const uploadRequestAttachment = async ({
  requestId,
  file,
  index,
  uploadedBy,
}) => {
  const safeFileName = `${Date.now()}_${index}_${file.name.replace(
    /[^a-zA-Z0-9._-]/g,
    "_",
  )}`;
  const storagePath = `featureRequests/${requestId}/${safeFileName}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: file.type || undefined,
  });

  return {
    fileName: file.name,
    savedFileName: safeFileName,
    storagePath,
    url: await getDownloadURL(storageRef),
    contentType: file.type || "",
    size: file.size || 0,
    uploadedAt: new Date().toISOString(),
    uploadedBy,
  };
};

const FeatureRequestList = () => {
  const { currentUser } = useAuth();
  const canManageStatus = canManageFeatureRequestStatus(currentUser);
  const canManageClassification =
    canManageFeatureRequestClassification(currentUser);
  const canDeleteRequests = isFeatureRequestAdmin(currentUser);

  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [attachmentPreviewRequest, setAttachmentPreviewRequest] =
    useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editSelectedFiles, setEditSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [isSavingClassification, setIsSavingClassification] = useState(false);
  const [isSavingAttachments, setIsSavingAttachments] = useState(false);
  const [removingAttachmentIndex, setRemovingAttachmentIndex] =
    useState(null);
  const [deletingRequestId, setDeletingRequestId] = useState("");
  const [statusDraft, setStatusDraft] = useState("New");
  const [resolutionCommentDraft, setResolutionCommentDraft] = useState("");
  const [priorityDraft, setPriorityDraft] = useState(
    DEFAULT_FEATURE_REQUEST_PRIORITY,
  );
  const [requestTypeDraft, setRequestTypeDraft] = useState("New Feature");
  const [searchText, setSearchText] = useState("");
  const [productAreaFilter, setProductAreaFilter] = useState("");
  const [requestTypeFilter, setRequestTypeFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const canEditSelectedRequest = canEditFeatureRequest(
    currentUser,
    selectedRequest,
  );

  useEffect(() => {
    const requestsQuery = query(
      collection(db, COLLECTION_NAME),
      orderBy("createdAt", "desc"),
    );

    const unsubscribe = onSnapshot(
      requestsQuery,
      (snapshot) => {
        setRequests(snapshot.docs.map((requestDoc) => ({
          id: requestDoc.id,
          ...requestDoc.data(),
        })));
        setIsLoading(false);
      },
      (error) => {
        console.error("Unable to load feature requests", error);
        notifyError("Unable to load requests");
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const filteredRequests = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    const matchingRequests = requests.filter((request) => {
      const searchableText = [
        request.title,
        request.description,
        request.productArea,
        request.requestType,
        request.createdBy,
        request.resolutionComment,
        request.resolutionCommentBy,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return (
        (!keyword || searchableText.includes(keyword)) &&
        (!productAreaFilter || request.productArea === productAreaFilter) &&
        (!requestTypeFilter || request.requestType === requestTypeFilter) &&
        (!priorityFilter || request.priority === priorityFilter) &&
        (!statusFilter || request.status === statusFilter)
      );
    });

    return sortFeatureRequestsForUser(matchingRequests, currentUser);
  }, [
    currentUser,
    productAreaFilter,
    priorityFilter,
    requestTypeFilter,
    requests,
    searchText,
    statusFilter,
  ]);

  const resetCreateForm = () => {
    setForm(EMPTY_FORM);
    setSelectedFiles([]);
  };

  const closeCreateModal = () => {
    if (isSubmitting) return;
    setShowCreateModal(false);
    resetCreateForm();
  };

  const openRequestDetail = (request) => {
    setSelectedRequest(request);
    setEditForm({
      title: request.title || "",
      description: request.description || "",
    });
    setEditSelectedFiles([]);
    setStatusDraft(request.status || "New");
    setPriorityDraft(
      PRIORITIES.includes(request.priority)
        ? request.priority
        : DEFAULT_FEATURE_REQUEST_PRIORITY,
    );
    setRequestTypeDraft(
      REQUEST_TYPES.includes(request.requestType)
        ? request.requestType
        : "New Feature",
    );
    setResolutionCommentDraft(request.resolutionComment || "");
  };

  const closeRequestDetail = () => {
    if (
      isSavingDetails ||
      isSavingAttachments ||
      isSavingStatus ||
      isSavingClassification
    ) {
      return;
    }

    setSelectedRequest(null);
    setEditForm({ title: "", description: "" });
    setEditSelectedFiles([]);
  };

  const openRequestAttachments = (request) => {
    setAttachmentPreviewRequest(request);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((previousForm) => ({ ...previousForm, [name]: value }));
  };

  const handleEditFormChange = (event) => {
    const { name, value } = event.target;
    setEditForm((previousForm) => ({ ...previousForm, [name]: value }));
  };

  const handleFileSelection = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles((previousFiles) => [...previousFiles, ...files]);
    event.target.value = "";
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((previousFiles) =>
      previousFiles.filter((file, fileIndex) => fileIndex !== index),
    );
  };

  const handleEditFileSelection = (event) => {
    const files = Array.from(event.target.files || []);
    setEditSelectedFiles((previousFiles) => [...previousFiles, ...files]);
    event.target.value = "";
  };

  const removeEditSelectedFile = (index) => {
    setEditSelectedFiles((previousFiles) =>
      previousFiles.filter((file, fileIndex) => fileIndex !== index),
    );
  };

  const handleCreateRequest = async (event) => {
    event.preventDefault();

    if (!form.title.trim() || !form.description.trim()) {
      notifyWarning("Please add a title and description");
      return;
    }

    setIsSubmitting(true);
    const successfullyUploaded = [];

    try {
      const requesterName =
        currentUser.displayName || currentUser.email || "Unknown user";
      const requestDocRef = doc(collection(db, COLLECTION_NAME));

      for (let index = 0; index < selectedFiles.length; index += 1) {
        const file = selectedFiles[index];
        const attachment = await uploadRequestAttachment({
          requestId: requestDocRef.id,
          file,
          index,
          uploadedBy: requesterName,
        });
        successfullyUploaded.push(attachment);
      }

      await setDoc(requestDocRef, {
        title: form.title.trim(),
        description: form.description.trim(),
        productArea: form.productArea,
        requestType: form.requestType,
        priority: resolveFeatureRequestPriority(currentUser, form.priority),
        status: "New",
        statusUpdatedBy: requesterName,
        statusUpdatedAt: serverTimestamp(),
        attachments: successfullyUploaded,
        createdBy: requesterName,
        createdByEmail: currentUser.email || "",
        createdByUid: currentUser.uid || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      notifyTopRight("Request created");
      setShowCreateModal(false);
      resetCreateForm();
    } catch (error) {
      console.error("Unable to create feature request", error);

      await Promise.all(
        successfullyUploaded.map((attachment) =>
          deleteObject(ref(storage, attachment.storagePath)).catch(() => null),
        ),
      );

      notifyError("Unable to create request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveRequestDetails = async () => {
    if (
      !selectedRequest ||
      !canEditFeatureRequest(currentUser, selectedRequest) ||
      isSavingDetails
    ) {
      return;
    }

    const title = editForm.title.trim();
    const description = editForm.description.trim();
    if (!title || !description) {
      notifyWarning("Please add a title and description");
      return;
    }

    if (
      title === String(selectedRequest.title || "").trim() &&
      description === String(selectedRequest.description || "").trim()
    ) {
      return;
    }

    const requestId = selectedRequest.id;
    setIsSavingDetails(true);
    try {
      await updateDoc(doc(db, COLLECTION_NAME, requestId), {
        title,
        description,
        updatedAt: serverTimestamp(),
      });
      setSelectedRequest((previousRequest) =>
        previousRequest && previousRequest.id === requestId
          ? { ...previousRequest, title, description }
          : previousRequest,
      );
      setEditForm({ title, description });
      notifyTopRight("Request details updated");
    } catch (error) {
      console.error("Unable to update request details", error);
      notifyError("Unable to update request details");
    } finally {
      setIsSavingDetails(false);
    }
  };

  const saveStatus = async () => {
    if (!selectedRequest || !canManageStatus) return;

    const needsResolutionComment =
      requiresFeatureRequestResolutionComment(statusDraft);
    const resolutionComment = resolutionCommentDraft.trim();
    if (needsResolutionComment && !resolutionComment) {
      notifyWarning(
        statusDraft === "Declined"
          ? "Please explain why this request was declined."
          : "Please describe what was fixed or completed.",
      );
      return;
    }

    setIsSavingStatus(true);
    try {
      const statusUpdatedBy =
        currentUser.displayName || currentUser.email || "Unknown user";
      const statusUpdate = {
        status: statusDraft,
        updatedAt: serverTimestamp(),
        statusUpdatedBy,
        statusUpdatedAt: serverTimestamp(),
      };

      if (needsResolutionComment) {
        statusUpdate.resolutionComment = resolutionComment;
        statusUpdate.resolutionCommentBy = statusUpdatedBy;
        statusUpdate.resolutionCommentAt = serverTimestamp();
      }

      await updateDoc(doc(db, COLLECTION_NAME, selectedRequest.id), statusUpdate);
      setSelectedRequest((previousRequest) => ({
        ...previousRequest,
        status: statusDraft,
        statusUpdatedBy,
        statusUpdatedAt: new Date(),
        ...(needsResolutionComment
          ? {
              resolutionComment,
              resolutionCommentBy: statusUpdatedBy,
              resolutionCommentAt: new Date(),
            }
          : {}),
      }));
      notifyTopRight("Request status updated");
    } catch (error) {
      console.error("Unable to update request status", error);
      notifyError("Unable to update request status");
    } finally {
      setIsSavingStatus(false);
    }
  };

  const saveClassification = async () => {
    if (!selectedRequest || !canManageClassification) return;

    const nextPriority = resolveFeatureRequestPriority(
      currentUser,
      priorityDraft,
    );
    const nextRequestType = REQUEST_TYPES.includes(requestTypeDraft)
      ? requestTypeDraft
      : "New Feature";
    if (
      nextPriority === selectedRequest.priority &&
      nextRequestType === selectedRequest.requestType
    ) {
      return;
    }

    setIsSavingClassification(true);
    try {
      await updateDoc(doc(db, COLLECTION_NAME, selectedRequest.id), {
        priority: nextPriority,
        requestType: nextRequestType,
        updatedAt: serverTimestamp(),
      });
      setSelectedRequest((previousRequest) => ({
        ...previousRequest,
        priority: nextPriority,
        requestType: nextRequestType,
      }));
      setPriorityDraft(nextPriority);
      setRequestTypeDraft(nextRequestType);
      notifyTopRight("Request type and priority updated");
    } catch (error) {
      console.error("Unable to update request type or priority", error);
      notifyError("Unable to update request type or priority");
    } finally {
      setIsSavingClassification(false);
    }
  };

  const uploadSelectedAttachments = async () => {
    if (
      !selectedRequest ||
      !canEditFeatureRequest(currentUser, selectedRequest) ||
      editSelectedFiles.length === 0 ||
      isSavingAttachments
    ) {
      return;
    }

    const requestId = selectedRequest.id;
    const existingAttachments = Array.isArray(selectedRequest.attachments)
      ? selectedRequest.attachments
      : [];
    const uploadedAttachments = [];

    setIsSavingAttachments(true);
    try {
      const uploadedBy =
        currentUser.displayName || currentUser.email || "Unknown user";

      for (let index = 0; index < editSelectedFiles.length; index += 1) {
        uploadedAttachments.push(
          await uploadRequestAttachment({
            requestId,
            file: editSelectedFiles[index],
            index,
            uploadedBy,
          }),
        );
      }

      const updatedAttachments = [
        ...existingAttachments,
        ...uploadedAttachments,
      ];
      await updateDoc(doc(db, COLLECTION_NAME, requestId), {
        attachments: updatedAttachments,
        updatedAt: serverTimestamp(),
      });

      setSelectedRequest((previousRequest) =>
        previousRequest && previousRequest.id === requestId
          ? { ...previousRequest, attachments: updatedAttachments }
          : previousRequest,
      );
      setEditSelectedFiles([]);
      notifyTopRight("Attachments added");
    } catch (error) {
      console.error("Unable to add request attachments", error);
      await Promise.all(
        uploadedAttachments.map((attachment) =>
          deleteObject(ref(storage, attachment.storagePath)).catch(() => null),
        ),
      );
      notifyError("Unable to add attachments");
    } finally {
      setIsSavingAttachments(false);
    }
  };

  const removeRequestAttachment = (attachment, index) => {
    if (
      !selectedRequest ||
      !canEditFeatureRequest(currentUser, selectedRequest) ||
      isSavingAttachments ||
      removingAttachmentIndex !== null
    ) {
      return;
    }

    swal({
      title: "Remove this attachment?",
      text: "This file will be removed from the request.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willRemove) => {
      if (!willRemove) return;

      const requestId = selectedRequest.id;
      const currentAttachments = Array.isArray(selectedRequest.attachments)
        ? selectedRequest.attachments
        : [];
      const updatedAttachments = currentAttachments.filter(
        (currentAttachment, attachmentIndex) => attachmentIndex !== index,
      );

      setRemovingAttachmentIndex(index);
      try {
        if (attachment && attachment.storagePath) {
          try {
            await deleteObject(ref(storage, attachment.storagePath));
          } catch (error) {
            if (error && error.code !== "storage/object-not-found") {
              throw error;
            }
          }
        }

        await updateDoc(doc(db, COLLECTION_NAME, requestId), {
          attachments: updatedAttachments,
          updatedAt: serverTimestamp(),
        });

        setSelectedRequest((previousRequest) =>
          previousRequest && previousRequest.id === requestId
            ? { ...previousRequest, attachments: updatedAttachments }
            : previousRequest,
        );
        if (
          previewFile &&
          attachment &&
          previewFile.storagePath === attachment.storagePath
        ) {
          setPreviewFile(null);
        }
        notifyTopRight("Attachment removed");
      } catch (error) {
        console.error("Unable to remove request attachment", error);
        notifyError("Unable to remove attachment");
      } finally {
        setRemovingAttachmentIndex(null);
      }
    });
  };

  const handleDeleteRequest = (request) => {
    if (!canDeleteRequests || !request) return;

    swal({
      title: "Delete this request?",
      text: "The request and its uploaded attachments will be permanently removed.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;

      setDeletingRequestId(request.id);
      try {
        const allFilesDeleted = await deleteStorageFolder(
          ref(storage, `featureRequests/${request.id}`),
        );

        if (!allFilesDeleted) {
          notifyError(
            "Unable to delete all attachments. The request was not deleted.",
          );
          return;
        }

        await deleteDoc(doc(db, COLLECTION_NAME, request.id));

        if (selectedRequest && selectedRequest.id === request.id) {
          setSelectedRequest(null);
        }
        if (
          attachmentPreviewRequest &&
          attachmentPreviewRequest.id === request.id
        ) {
          setAttachmentPreviewRequest(null);
        }

        notifyTopRight("Request and all attachments deleted");
      } catch (error) {
        console.error("Unable to delete request", error);
        notifyError("Unable to delete request");
      } finally {
        setDeletingRequestId("");
      }
    });
  };

  const openAttachmentPreview = async (attachment) => {
    try {
      const url =
        attachment.url ||
        (attachment.storagePath
          ? await getDownloadURL(ref(storage, attachment.storagePath))
          : "");

      if (!url) {
        notifyError("Cannot open this attachment");
        return;
      }

      setPreviewFile({ ...attachment, url });
    } catch (error) {
      console.error("Unable to open attachment", error);
      notifyError("Cannot open this attachment");
    }
  };

  const renderPreviewContent = () => {
    if (!previewFile) return null;

    if (getFileExtension(previewFile) === "pdf") {
      return (
        <iframe
          title="Request attachment preview"
          src={previewFile.url}
          style={{ width: "100%", height: "72vh", border: "none" }}
        />
      );
    }

    if (isImageFile(previewFile)) {
      return (
        <div
          className="d-flex justify-content-center align-items-center bg-dark"
          style={{ minHeight: "72vh" }}
        >
          <img
            src={previewFile.url}
            alt={previewFile.fileName || "Request attachment"}
            style={{ maxWidth: "100%", maxHeight: "72vh", objectFit: "contain" }}
          />
        </div>
      );
    }

    if (isOfficeFile(previewFile)) {
      const officeViewerUrl =
        "https://view.officeapps.live.com/op/embed.aspx?src=" +
        encodeURIComponent(previewFile.url);

      return (
        <iframe
          title="Request attachment preview"
          src={officeViewerUrl}
          style={{ width: "100%", height: "72vh", border: "none" }}
        />
      );
    }

    return (
      <div className="text-center py-5">
        <p className="text-muted mb-3">
          This file type cannot be previewed in the browser.
        </p>
        <Button
          variant="primary"
          onClick={() => window.open(previewFile.url, "_blank")}
        >
          Open File
        </Button>
      </div>
    );
  };

  return (
    <Fragment>
      <PageTitle
        motherMenu="Product Requests"
        activeMenu="Request List"
        motherLink="/feature-request-list"
      />
      <Toast />

      <div className="form-head d-flex flex-wrap mb-3 align-items-start">
        <div className="mr-auto mb-2">
          <h4 className="mb-1">Product Requests</h4>
          <p className="text-muted mb-0">
            Track BDSleep, PSG BDT, and hardware feature requests or bug fixes.
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          <i className="fa fa-plus mr-2" /> Create New Request
        </Button>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-lg-4 mb-2">
              <input
                className="form-control"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search title, description, or requester"
              />
            </div>
            <div className="col-lg-2 col-md-4 mb-2">
              <select
                className="form-control"
                value={productAreaFilter}
                onChange={(event) => setProductAreaFilter(event.target.value)}
              >
                <option value="">All areas</option>
                {PRODUCT_AREAS.map((area) => (
                  <option key={area} value={area}>
                    {area}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-2 col-md-4 mb-2">
              <select
                className="form-control"
                value={requestTypeFilter}
                onChange={(event) => setRequestTypeFilter(event.target.value)}
              >
                <option value="">All request types</option>
                {REQUEST_TYPES.map((requestType) => (
                  <option key={requestType} value={requestType}>
                    {requestType}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-2 col-md-4 mb-2">
              <select
                className="form-control"
                value={priorityFilter}
                onChange={(event) => setPriorityFilter(event.target.value)}
              >
                <option value="">All priorities</option>
                {PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-2 col-md-4 mb-2">
              <select
                className="form-control"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="">All statuses</option>
                {STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-striped mb-0">
              <thead>
                <tr>
                  <th>Created / Submitted By</th>
                  <th>Request</th>
                  <th>Area / Type / Files</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Resolution</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      Loading requests...
                    </td>
                  </tr>
                ) : filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      No requests found.
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => (
                    <tr key={request.id}>
                      <td style={{ minWidth: 155 }}>
                        <div className="text-nowrap">
                          {formatDate(request.createdAt)}
                        </div>
                        <small className="d-block text-muted mt-1">
                          by {request.createdBy || "-"}
                        </small>
                      </td>
                      <td style={{ minWidth: 260, maxWidth: 360 }}>
                        <div className="font-weight-bold">{request.title}</div>
                        <div
                          className="text-muted text-truncate"
                          title={request.description || ""}
                        >
                          {request.description || "-"}
                        </div>
                      </td>
                      <td style={{ minWidth: 165, maxWidth: 220 }}>
                        <div className="text-truncate" title={request.productArea || "-"}>
                          <span className="text-muted">Area:</span>{" "}
                          {request.productArea || "-"}
                        </div>
                        <div
                          className="text-truncate mt-1"
                          title={request.requestType || "-"}
                        >
                          <span className="text-muted">Type:</span>{" "}
                          {request.requestType || "-"}
                        </div>
                        {(request.attachments || []).length > 0 ? (
                          <button
                            type="button"
                            className="btn btn-link text-muted p-0 mt-1"
                            onClick={() => openRequestAttachments(request)}
                            aria-label={`View ${
                              (request.attachments || []).length
                            } attachment${
                              (request.attachments || []).length === 1
                                ? ""
                                : "s"
                            } for ${request.title}`}
                          >
                            <i className="fa fa-paperclip mr-1" />
                            Files: {(request.attachments || []).length} file
                            {(request.attachments || []).length === 1
                              ? ""
                              : "s"}
                          </button>
                        ) : (
                          <small className="d-block text-muted mt-1">
                            <i className="fa fa-paperclip mr-1" />
                            Files: 0 files
                          </small>
                        )}
                      </td>
                      <td>
                        <Badge variant={priorityVariant(request.priority)}>
                          {request.priority || "-"}
                        </Badge>
                      </td>
                      <td>
                        <Badge variant={statusVariant(request.status)}>
                          {request.status || "New"}
                        </Badge>
                        <small className="d-block text-muted mt-1">
                          by {request.statusUpdatedBy || request.createdBy || "-"}
                        </small>
                        <small className="d-block text-muted">
                          Updated: {formatDate(
                            request.statusUpdatedAt || request.createdAt,
                          )}
                        </small>
                      </td>
                      <td style={{ minWidth: 220, maxWidth: 320 }}>
                        {request.resolutionComment ? (
                          <>
                            <div
                              className="text-truncate"
                              title={request.resolutionComment}
                            >
                              {request.resolutionComment}
                            </div>
                            <small className="d-block text-muted mt-1">
                              by {request.resolutionCommentBy || "-"}
                            </small>
                            <small className="d-block text-muted">
                              {formatDate(request.resolutionCommentAt)}
                            </small>
                          </>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-right">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openRequestDetail(request)}
                        >
                          {canEditFeatureRequest(currentUser, request)
                            ? "View / Edit"
                            : "View"}
                        </Button>
                        {canDeleteRequests && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="ml-2"
                            onClick={() => handleDeleteRequest(request)}
                            disabled={deletingRequestId === request.id}
                          >
                            {deletingRequestId === request.id ? "Deleting..." : "Delete"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal show={showCreateModal} onHide={closeCreateModal} size="lg">
        <form onSubmit={handleCreateRequest}>
          <Modal.Header closeButton>
            <Modal.Title>Create New Request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted">
              Give the team enough detail to review, reproduce, and plan this work.
            </p>
            <div className="form-group">
              <label htmlFor="feature-request-title">Title *</label>
              <input
                id="feature-request-title"
                name="title"
                className="form-control"
                value={form.title}
                onChange={handleFormChange}
                placeholder="Example: Add patient report download to the PSG BDT screen"
                maxLength="180"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="feature-request-description">Description *</label>
              <textarea
                id="feature-request-description"
                name="description"
                className="form-control"
                rows="5"
                value={form.description}
                onChange={handleFormChange}
                placeholder="Describe the current situation, the requested result, and any useful steps or examples."
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group col-md-4">
                <label htmlFor="feature-request-area">Product Area *</label>
                <select
                  id="feature-request-area"
                  name="productArea"
                  className="form-control"
                  value={form.productArea}
                  onChange={handleFormChange}
                >
                  {PRODUCT_AREAS.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group col-md-4">
                <label htmlFor="feature-request-type">Request Type *</label>
                <select
                  id="feature-request-type"
                  name="requestType"
                  className="form-control"
                  value={form.requestType}
                  onChange={handleFormChange}
                >
                  {REQUEST_TYPES.map((requestType) => (
                    <option key={requestType} value={requestType}>
                      {requestType}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group col-md-4">
                <label htmlFor="feature-request-priority">Priority *</label>
                <select
                  id="feature-request-priority"
                  name="priority"
                  className="form-control"
                  value={form.priority}
                  onChange={handleFormChange}
                  disabled={!canManageClassification}
                >
                  {PRIORITIES.map((priority) => (
                    <option key={priority} value={priority}>
                      {priority}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  {canManageClassification
                    ? "Critical and high priorities are highlighted in the request list."
                    : "New requests start at Medium. Only admin888 can change priority."}
                </small>
              </div>
            </div>
            <small className="form-text text-muted mt-n2 mb-3">
              Choose “Other / Not sure” when you are unsure how to classify the request.
            </small>
            <div className="form-group mb-0">
              <label htmlFor="feature-request-files">Attachments</label>
              <input
                id="feature-request-files"
                type="file"
                className="form-control-file"
                multiple
                accept="image/*,application/pdf,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.doc,.docx,.xls,.xlsx"
                onChange={handleFileSelection}
              />
              <small className="form-text text-muted">
                You may attach multiple images, PDFs, Word documents, or Excel files.
              </small>
              {selectedFiles.length > 0 && (
                <ul className="list-group mt-2">
                  {selectedFiles.map((file, index) => (
                    <li
                      key={`${file.name}-${file.size}-${index}`}
                      className="list-group-item d-flex align-items-center py-2"
                    >
                      <span className="text-truncate mr-2">{file.name}</span>
                      <button
                        type="button"
                        className="btn btn-link text-danger ml-auto p-0"
                        onClick={() => removeSelectedFile(index)}
                        aria-label={`Remove ${file.name}`}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={closeCreateModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Submit Request"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal
        show={Boolean(selectedRequest)}
        onHide={closeRequestDetail}
        size="lg"
      >
        {selectedRequest && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{selectedRequest.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-4" style={{ whiteSpace: "pre-wrap" }}>
                {selectedRequest.description}
              </div>
              <div className="row mb-3">
                <div className="col-md-4 mb-2">
                  <small className="text-muted d-block">Product Area</small>
                  {selectedRequest.productArea || "-"}
                </div>
                <div className="col-md-4 mb-2">
                  <small className="text-muted d-block">Request Type</small>
                  {selectedRequest.requestType || "-"}
                </div>
                <div className="col-md-4 mb-2">
                  <small className="text-muted d-block">Priority</small>
                  <Badge variant={priorityVariant(selectedRequest.priority)}>
                    {selectedRequest.priority || "-"}
                  </Badge>
                </div>
                <div className="col-md-4 mb-2">
                  <small className="text-muted d-block">Submitted By</small>
                  {selectedRequest.createdBy || "-"}
                </div>
                <div className="col-md-4 mb-2">
                  <small className="text-muted d-block">Created</small>
                  {formatDate(selectedRequest.createdAt)}
                </div>
                <div className="col-md-4 mb-2">
                  <small className="text-muted d-block">Current Status</small>
                  <Badge variant={statusVariant(selectedRequest.status)}>
                    {selectedRequest.status || "New"}
                  </Badge>
                </div>
                <div className="col-md-4 mb-2">
                  <small className="text-muted d-block">Status Updated By</small>
                  {selectedRequest.statusUpdatedBy || selectedRequest.createdBy || "-"}
                </div>
                <div className="col-md-4 mb-2">
                  <small className="text-muted d-block">Status Updated</small>
                  {formatDate(
                    selectedRequest.statusUpdatedAt || selectedRequest.createdAt,
                  )}
                </div>
                {selectedRequest.resolutionComment && (
                  <div className="col-12 mb-2">
                    <small className="text-muted d-block">
                      {selectedRequest.status === "Declined"
                        ? "Decline Reason"
                        : "Resolution Comment"}
                    </small>
                    <div style={{ whiteSpace: "pre-wrap" }}>
                      {selectedRequest.resolutionComment}
                    </div>
                    <small className="text-muted d-block mt-1">
                      by {selectedRequest.resolutionCommentBy || "-"}
                      {selectedRequest.resolutionCommentAt
                        ? ` · ${formatDate(selectedRequest.resolutionCommentAt)}`
                        : ""}
                    </small>
                  </div>
                )}
              </div>

              {canEditSelectedRequest && (
                <div className="border rounded p-3 mb-4 bg-light">
                  <div className="font-weight-bold mb-2">
                    Edit request details
                  </div>
                  <div className="form-group">
                    <label htmlFor="request-edit-title">Title *</label>
                    <input
                      id="request-edit-title"
                      name="title"
                      className="form-control"
                      value={editForm.title}
                      onChange={handleEditFormChange}
                      maxLength="180"
                      disabled={isSavingDetails}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="request-edit-description">
                      Description *
                    </label>
                    <textarea
                      id="request-edit-description"
                      name="description"
                      className="form-control"
                      rows="5"
                      value={editForm.description}
                      onChange={handleEditFormChange}
                      disabled={isSavingDetails}
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={saveRequestDetails}
                    disabled={
                      isSavingDetails ||
                      (editForm.title.trim() ===
                        String(selectedRequest.title || "").trim() &&
                        editForm.description.trim() ===
                          String(selectedRequest.description || "").trim())
                    }
                  >
                    {isSavingDetails ? "Saving..." : "Save Request Details"}
                  </Button>
                </div>
              )}

              {canManageStatus && (
                <div className="border rounded p-3 mb-4 bg-light">
                  <label className="font-weight-bold mb-2" htmlFor="request-status">
                    Update task status
                  </label>
                  <div className="d-flex flex-wrap align-items-center">
                    <select
                      id="request-status"
                      className="form-control mr-2 mb-2"
                      style={{ maxWidth: 220 }}
                      value={statusDraft}
                      onChange={(event) => setStatusDraft(event.target.value)}
                    >
                      {STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="primary"
                      className="mb-2"
                      onClick={saveStatus}
                      disabled={
                        isSavingStatus ||
                        (statusDraft === selectedRequest.status &&
                          (!requiresFeatureRequestResolutionComment(statusDraft) ||
                            resolutionCommentDraft.trim() ===
                              String(
                                selectedRequest.resolutionComment || "",
                              ).trim()))
                      }
                    >
                      {isSavingStatus ? "Saving..." : "Save Status"}
                    </Button>
                  </div>
                  {requiresFeatureRequestResolutionComment(statusDraft) && (
                    <div className="form-group mb-0 mt-1">
                      <label htmlFor="request-resolution-comment">
                        {statusDraft === "Declined"
                          ? "Why was this request declined? *"
                          : "What was fixed or completed? *"}
                      </label>
                      <textarea
                        id="request-resolution-comment"
                        className="form-control"
                        rows="3"
                        maxLength={MAX_RESOLUTION_COMMENT_LENGTH}
                        value={resolutionCommentDraft}
                        onChange={(event) =>
                          setResolutionCommentDraft(event.target.value)
                        }
                        placeholder={
                          statusDraft === "Declined"
                            ? "Explain why this request cannot be completed."
                            : "Describe the fix, completion details, or the cause of the issue."
                        }
                        required
                      />
                      <small className="form-text text-muted">
                        This note is shown with the status, including who saved it
                        and when.
                      </small>
                    </div>
                  )}
                </div>
              )}

              {canManageClassification && (
                <div className="border rounded p-3 mb-4 bg-light">
                  <div className="font-weight-bold mb-2">
                    Update request classification
                  </div>
                  <div className="form-row align-items-end">
                    <div className="form-group col-md-5 mb-2">
                      <label htmlFor="request-type">Request Type</label>
                      <select
                        id="request-type"
                        className="form-control"
                        value={requestTypeDraft}
                        onChange={(event) =>
                          setRequestTypeDraft(event.target.value)
                        }
                      >
                        {REQUEST_TYPES.map((requestType) => (
                          <option key={requestType} value={requestType}>
                            {requestType}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group col-md-4 mb-2">
                      <label htmlFor="request-priority">Priority</label>
                      <select
                        id="request-priority"
                        className="form-control"
                        value={priorityDraft}
                        onChange={(event) =>
                          setPriorityDraft(event.target.value)
                        }
                      >
                        {PRIORITIES.map((priority) => (
                          <option key={priority} value={priority}>
                            {priority}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group col-md-3 mb-2">
                      <Button
                        variant="primary"
                        className="w-100"
                        onClick={saveClassification}
                        disabled={
                          isSavingClassification ||
                          (priorityDraft === selectedRequest.priority &&
                            requestTypeDraft === selectedRequest.requestType)
                        }
                      >
                        {isSavingClassification
                          ? "Saving..."
                          : "Save Changes"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <h6 className="mb-2">Attachments</h6>
              {canEditSelectedRequest && (
                <div className="border rounded p-3 mb-3 bg-light">
                  <label
                    className="font-weight-bold"
                    htmlFor="feature-request-edit-files"
                  >
                    Add attachments
                  </label>
                  <input
                    id="feature-request-edit-files"
                    type="file"
                    className="form-control-file"
                    multiple
                    accept="image/*,application/pdf,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.doc,.docx,.xls,.xlsx"
                    onChange={handleEditFileSelection}
                    disabled={
                      isSavingAttachments || removingAttachmentIndex !== null
                    }
                  />
                  <small className="form-text text-muted">
                    Add images or other supporting files to this request.
                  </small>
                  {editSelectedFiles.length > 0 && (
                    <>
                      <ul className="list-group mt-2">
                        {editSelectedFiles.map((file, index) => (
                          <li
                            key={`${file.name}-${file.size}-${index}`}
                            className="list-group-item d-flex align-items-center py-2"
                          >
                            <span className="text-truncate mr-2">
                              {file.name}
                            </span>
                            <button
                              type="button"
                              className="btn btn-link text-danger ml-auto p-0"
                              onClick={() => removeEditSelectedFile(index)}
                              disabled={isSavingAttachments}
                              aria-label={`Remove ${file.name}`}
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                      <Button
                        variant="primary"
                        size="sm"
                        className="mt-2"
                        onClick={uploadSelectedAttachments}
                        disabled={isSavingAttachments}
                      >
                        {isSavingAttachments
                          ? "Adding..."
                          : "Add Selected Attachments"}
                      </Button>
                    </>
                  )}
                </div>
              )}
              {(selectedRequest.attachments || []).length === 0 ? (
                <p className="text-muted mb-0">No attachments were added.</p>
              ) : (
                <div className="list-group">
                  {selectedRequest.attachments.map((attachment, index) => (
                    <div
                      key={attachment.storagePath || `${attachment.fileName}-${index}`}
                      className="list-group-item d-flex align-items-center"
                    >
                      <button
                        type="button"
                        className="btn btn-link text-left p-0 d-flex align-items-center flex-grow-1"
                        onClick={() => openAttachmentPreview(attachment)}
                      >
                        <i className="fa fa-paperclip mr-2 text-primary" />
                        <span className="text-truncate">
                          {attachment.fileName || attachment.savedFileName}
                        </span>
                        <span className="ml-auto text-muted small">Open</span>
                      </button>
                      {canEditSelectedRequest && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="ml-3"
                          onClick={() =>
                            removeRequestAttachment(attachment, index)
                          }
                          disabled={
                            isSavingAttachments ||
                            removingAttachmentIndex !== null
                          }
                          aria-label={`Remove ${attachment.fileName || attachment.savedFileName}`}
                        >
                          {removingAttachmentIndex === index
                            ? "Removing..."
                            : "Remove"}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="light" onClick={closeRequestDetail}>
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <Modal
        show={Boolean(attachmentPreviewRequest)}
        onHide={() => setAttachmentPreviewRequest(null)}
        size="lg"
      >
        {attachmentPreviewRequest && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>Attachments</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="font-weight-bold mb-1">
                {attachmentPreviewRequest.title}
              </p>
              <p className="text-muted mb-3">
                Select a file to preview it.
              </p>
              <div className="list-group">
                {(attachmentPreviewRequest.attachments || []).map(
                  (attachment, index) => (
                    <button
                      type="button"
                      key={
                        attachment.storagePath ||
                        `${attachment.fileName}-${index}`
                      }
                      className="list-group-item list-group-item-action d-flex align-items-center text-left"
                      onClick={() => openAttachmentPreview(attachment)}
                    >
                      <i className="fa fa-paperclip mr-2 text-primary" />
                      <span className="text-truncate">
                        {attachment.fileName || attachment.savedFileName}
                      </span>
                      <span className="ml-auto text-muted small">Preview</span>
                    </button>
                  ),
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="light"
                onClick={() => setAttachmentPreviewRequest(null)}
              >
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <Modal show={Boolean(previewFile)} onHide={() => setPreviewFile(null)} size="xl">
        {previewFile && (
          <>
            <Modal.Header closeButton>
              <Modal.Title className="text-truncate">{previewFile.fileName}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">{renderPreviewContent()}</Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-primary"
                onClick={() => window.open(previewFile.url, "_blank")}
              >
                Open New Tab
              </Button>
              <Button variant="light" onClick={() => setPreviewFile(null)}>
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </Fragment>
  );
};

export default FeatureRequestList;
